/**
 * Case Raw Model (Schema v2.0.0)
 *
 * Type definitions for raw case data collected from official manufacturer sources.
 * Based on scripts/case/pc-case-schema-v2.ts and scripts/shared/types/common.ts
 */

// ===== SCHEMA VERSION =====

export type SchemaVersion = '2.0.0'

// ===== UNION TYPES =====

/**
 * PC Case form factor/size classification
 */
export type CaseFormFactor =
  | 'Super-Tower'
  | 'Full-Tower'
  | 'Mid-Tower'
  | 'Mini-Tower'
  | 'Small-Form-Factor'
  | 'Cube'
  | 'HTPC'
  | 'Desktop'

/**
 * Material types for case construction
 */
export type CaseMaterial =
  | 'Steel'
  | 'Aluminum'
  | 'Tempered Glass'
  | 'Acrylic'
  | 'Mesh'
  | 'Plastic'
  | 'Wood'

/**
 * Fan sizes in millimeters
 */
export type FanSize = 80 | 92 | 120 | 140 | 180 | 200

/**
 * Radiator sizes (width in mm)
 */
export type RadiatorSize = 120 | 140 | 240 | 280 | 360 | 420 | 480

/**
 * USB port types
 */
export type UsbType =
  | 'USB 2.0'
  | 'USB 3.0'
  | 'USB 3.1'
  | 'USB 3.2'
  | 'USB 3.2 Gen 2x2'
  | 'USB4'
  | 'Thunderbolt 3'
  | 'Thunderbolt 4'
  | 'Thunderbolt 5'

/**
 * USB connector types
 */
export type UsbConnector = 'Type-A' | 'Type-C'

/**
 * PSU form factors
 */
export type PsuFormFactor = 'ATX' | 'SFX' | 'SFX-L' | 'TFX' | 'Flex ATX'

/**
 * PSU mounting position
 */
export type PsuPosition = 'Top' | 'Bottom'

/**
 * PSU fan orientation
 */
export type PsuOrientation = 'Fan-Up' | 'Fan-Down' | 'Both'

/**
 * Radiator/Fan mounting locations
 */
export type MountLocation = 'Front' | 'Top' | 'Rear' | 'Bottom' | 'Side'

/**
 * Motherboard form factors (aligned with scripts/shared/types/common.ts)
 */
export type MotherboardFormFactor =
  | 'E-ATX'
  | 'ATX'
  | 'Micro-ATX'
  | 'Mini-ITX'
  | 'SSI-CEB'
  | 'SSI-EEB'
  | 'DTX'
  | 'Mini-DTX'
  | 'STX'
  | 'Thin Mini-ITX'

/**
 * Audio connector types (aligned with scripts/shared/types/common.ts)
 */
export type AudioConnectorType =
  | 'HD Audio'
  | "AC'97"
  | 'Headphone'
  | 'Microphone'
  | 'Combo'
  | 'Optical'
  | 'Coaxial'

// ===== COMMON TYPES =====

/**
 * Dimension in millimeters (standardized unit)
 * All measurements are standardized to mm for consistency
 */
export interface Dimension {
  /** Value in millimeters */
  value: number
  /** Unit is always 'mm' for standardization */
  unit: 'mm'
  /** Original raw text if unit conversion was needed */
  _raw?: string
}

/**
 * Weight in kilograms (standardized unit)
 * All weights are standardized to kg for consistency
 */
export interface Weight {
  /** Value in kilograms */
  value: number
  /** Unit is always 'kg' for standardization */
  unit: 'kg'
  /** Original raw text if unit conversion was needed */
  _raw?: string
}

// ===== COMPONENT TYPES =====

/**
 * Manufacturer information
 */
export interface ManufacturerInfo {
  name: string
  country?: string
  website?: string
}

/**
 * Case physical dimensions
 */
export interface CaseDimensions {
  height: Dimension
  width: Dimension
  depth: Dimension
  volume?: number
  withFeet?: {
    height?: Dimension
    width?: Dimension
    depth?: Dimension
  }
  _raw?: string
  notes?: string
}

/**
 * Motherboard compatibility specification
 */
export interface MotherboardCompatibility {
  formFactors: MotherboardFormFactor[]
  maxWidth?: number
  _raw?: string
}

/**
 * Drive bay specification
 */
export interface DriveBays {
  hdd35?: number
  ssd25?: number
  combo?: number
  external525?: number
  toolLess?: boolean
  hotSwap?: boolean
  _raw?: string
  notes?: string
}

/**
 * Expansion slot specification
 */
export interface ExpansionSlots {
  total: number
  verticalGpu?: boolean
  maxGpuLength?: Dimension
  maxGpuHeight?: Dimension
  _raw?: string
  notes?: string
}

/**
 * Fan mounting specification
 */
export interface FanMount {
  location: MountLocation
  size: FanSize
  maxCount: number
  preInstalled?: number
  notes?: string
}

/**
 * Radiator mounting specification
 */
export interface RadiatorMount {
  location: MountLocation
  maxSize: RadiatorSize
  maxThickness?: number
  _raw?: string
  notes?: string
}

/**
 * Cooling system specification
 */
export interface CoolingSpec {
  fanMounts: FanMount[]
  radiatorMounts: RadiatorMount[]
  maxCpuCoolerHeight?: Dimension
  _raw?: string
}

/**
 * Audio port specification (aligned with scripts/shared/types/common.ts)
 */
export interface AudioPort {
  /** Type of audio connector */
  type: AudioConnectorType
  /** Number of ports of this type */
  count: number
  /** Notes for unusual audio connector types */
  notes?: string
}

/**
 * RGB/ARGB Header Specification
 * Represents RGB/ARGB headers PROVIDED by a component
 */
export interface RgbHeaderSpec {
  /** Number of 5V 3-pin ARGB headers provided */
  argbHeaders?: number
  /** Number of 12V 4-pin RGB headers provided */
  rgbHeaders?: number
  /** Notes for unusual RGB headers */
  notes?: string
}

/**
 * RGB Device Specification
 * Represents RGB/ARGB devices that REQUIRE connection to RGB headers
 */
export interface RgbDeviceSpec {
  /** Number of pre-installed 5V ARGB devices */
  argbDevices?: number
  /** Number of pre-installed 12V RGB devices */
  rgbDevices?: number
  /** Built-in RGB controller */
  hasBuiltInController?: boolean
  /** Notes */
  notes?: string
}

/**
 * Front I/O specification
 */
export interface FrontIO {
  usb: {
    type: UsbType
    connector: UsbConnector
    count: number
  }[]
  audio: AudioPort[]
  powerButton?: boolean
  resetButton?: boolean
  other?: string[]
  _raw?: string
}

/**
 * Build material specification
 */
export interface BuildMaterial {
  chassis?: CaseMaterial[]
  sidePanels?: CaseMaterial[]
  frontPanel?: CaseMaterial[]
  topPanel?: CaseMaterial[]
  psuShroud?: boolean
  _raw?: string
}

/**
 * Dust filter specification
 */
export interface DustFilters {
  front?: boolean
  top?: boolean
  bottom?: boolean
  rear?: boolean
  removable?: boolean
  notes?: string
}

/**
 * Cable management features
 */
export interface CableManagement {
  cableRoutingHoles?: boolean
  velcroStraps?: number
  tieDownPoints?: number
  behindMotherboardTray?: Dimension
  psuShroud?: boolean
  notes?: string
}

/**
 * Power supply specification
 */
export interface PsuSpec {
  position?: PsuPosition
  orientation?: PsuOrientation
  maxLength?: Dimension
  formFactors?: PsuFormFactor[]
  _raw?: string
}

/**
 * RGB/Lighting specification
 */
export interface RgbSpec {
  /** Pre-installed RGB devices that need connection */
  devices?: RgbDeviceSpec
  /** RGB controller and headers provided by the case */
  controller?: RgbHeaderSpec
  /** LED strips included (deprecated - use devices instead) */
  ledStrips?: boolean
  /** Notes */
  notes?: string
}

// ===== MAIN MODEL =====

/**
 * PC Case Specification (Schema v2.0.0)
 *
 * Complete case specification data collected from official sources.
 */
export interface CaseRawModel {
  /** Schema version */
  schemaVersion: SchemaVersion

  /** Unique identifier */
  id: string

  /** Manufacturer information */
  manufacturer: ManufacturerInfo

  /** Product name */
  productName: string

  /** Model number/SKU */
  modelNumber?: string

  /** Product series/family */
  series?: string

  /** Official product URL */
  officialUrl?: string

  /** Amazon ASIN */
  asin?: string

  /** Case form factor */
  formFactor?: CaseFormFactor

  /** Motherboard compatibility */
  motherboardCompatibility: MotherboardCompatibility

  /** Physical dimensions */
  dimensions: CaseDimensions

  /** Weight */
  weight?: Weight

  /** Color */
  color?: string

  /** Drive bays */
  driveBays?: DriveBays

  /** Expansion slots */
  expansionSlots?: ExpansionSlots

  /** Cooling system */
  cooling?: CoolingSpec

  /** Front I/O */
  frontIO?: FrontIO

  /** Build materials */
  material?: BuildMaterial

  /** Dust filters */
  dustFilters?: DustFilters

  /** Cable management */
  cableManagement?: CableManagement

  /** PSU specification */
  psu?: PsuSpec

  /** RGB/Lighting */
  rgb?: RgbSpec

  /** Warranty period in years */
  warranty?: number

  /** Release date */
  releaseDate?: {
    year: number
    month?: number
  }

  /** Notes for unusual data */
  notes?: string

  /** AI confidence score */
  _confidence?: number
}

// ===== TYPE GUARDS =====

/**
 * Check if object is a valid CaseRawModel
 */
export function isCaseRawModel(obj: unknown): obj is CaseRawModel {
  if (typeof obj !== 'object' || obj === null) return false

  const raw = obj as Record<string, unknown>

  const hasIdentity =
    typeof raw.id === 'string' &&
    typeof raw.manufacturer === 'object' &&
    raw.manufacturer !== null &&
    typeof (raw.manufacturer as Record<string, unknown>).name === 'string' &&
    typeof raw.productName === 'string'

  const hasDimensions =
    typeof raw.dimensions === 'object' &&
    raw.dimensions !== null

  const hasMotherboardCompatibility =
    typeof raw.motherboardCompatibility === 'object' &&
    raw.motherboardCompatibility !== null &&
    Array.isArray((raw.motherboardCompatibility as Record<string, unknown>).formFactors)

  return hasIdentity && hasDimensions && hasMotherboardCompatibility
}

// ===== UTILITY FUNCTIONS =====

/**
 * Calculate volume in liters from dimensions
 */
export function calculateVolume(dimensions: CaseDimensions): number {
  const height = dimensions.height.value
  const width = dimensions.width.value
  const depth = dimensions.depth.value
  return Math.round((height * width * depth) / 1000000 * 10) / 10
}

/**
 * Get manufacturer name
 */
export function getManufacturerName(raw: CaseRawModel): string {
  return raw.manufacturer.name
}

/**
 * Get total pre-installed fan count
 */
export function getPreInstalledFanCount(raw: CaseRawModel): number {
  if (!raw.cooling?.fanMounts) return 0
  return raw.cooling.fanMounts.reduce((total, mount) => {
    return total + (mount.preInstalled ?? 0)
  }, 0)
}

/**
 * Get maximum GPU length in mm
 */
export function getMaxGpuLength(raw: CaseRawModel): number {
  return raw.expansionSlots?.maxGpuLength?.value ?? 0
}

/**
 * Get maximum CPU cooler height in mm
 */
export function getMaxCpuCoolerHeight(raw: CaseRawModel): number {
  return raw.cooling?.maxCpuCoolerHeight?.value ?? 0
}

/**
 * Get maximum PSU length in mm
 */
export function getMaxPsuLength(raw: CaseRawModel): number {
  return raw.psu?.maxLength?.value ?? 0
}

/**
 * Get maximum radiator size supported
 */
export function getMaxRadiatorSize(raw: CaseRawModel): number {
  if (!raw.cooling?.radiatorMounts?.length) return 0
  return Math.max(...raw.cooling.radiatorMounts.map(m => m.maxSize))
}

/**
 * Get expansion slot count
 */
export function getExpansionSlotCount(raw: CaseRawModel): number {
  return raw.expansionSlots?.total ?? 0
}

/**
 * Get USB port counts by connector type
 */
export function getUSBPortCounts(raw: CaseRawModel): {
  typeA: number
  typeC: number
  total: number
} {
  let typeA = 0
  let typeC = 0

  if (raw.frontIO?.usb) {
    for (const port of raw.frontIO.usb) {
      if (port.connector === 'Type-A') {
        typeA += port.count
      } else if (port.connector === 'Type-C') {
        typeC += port.count
      }
    }
  }

  return { typeA, typeC, total: typeA + typeC }
}
